// src/services/timesheetsService.ts
// Fetches live timesheets from the Cloudflare Worker API (Neon database).
// Maps to the TS shape Timesheets.tsx expects.
//
// Architecture note:
//   The Timesheets page uses a mutable useState(MOCK_TIMESHEETS) pattern because
//   users actively edit entries, approve, reject, and export on the page.
//   This service feeds the seed-from-API approach: on first load the component
//   merges Neon timesheets (deduplicating by ID) into its local state, then all
//   subsequent mutations continue to work against that merged state.
//
// Field notes:
//   timesheets.weekStart    → weekStarting (Date ISO → YYYY-MM-DD string)
//   timesheets.status       → status (EXPORTED → PROCESSED for UI compat)
//   employee.name           → employeeName (joined from employees table)
//   entries                 → [] (empty; entry-level wiring is Phase 6 step 2)
//   hoursRegular/Overtime   → mapped but not used by the rich UI entry cells

import { api } from '../utils/api'

// What the API returns — with employee name joined
interface ApiTimesheet {
  id:            string
  employeeId:    string
  employee_id?:  string
  // Employee name joined from employees table
  employee?:     { name?: string } | null
  jobId?:        string | null
  job_id?:       string | null
  weekStart?:    string | null    // ISO date string from Neon
  week_start?:   string | null
  shift?:        string | null
  status?:       string
  hoursRegular?:  string | number | null
  hours_regular?: string | number | null
  hoursOvertime?: string | number | null
  hours_overtime?: string | number | null
  hoursTotal?:    string | number | null
  hours_total?:   string | number | null
  grossPay?:      string | number | null
  gross_pay?:     string | number | null
  paygAmount?:    string | number | null
  payg_amount?:   string | number | null
  superAmount?:   string | number | null
  super_amount?:  string | number | null
  approvedById?:  string | null
  approved_by_id?: string | null
  approvedAt?:    string | null
  approved_at?:   string | null
  abaExportedAt?: string | null
  aba_exported_at?: string | null
  stpLodgedAt?:   string | null
  stp_lodged_at?: string | null
  createdAt?:     string
  created_at?:    string
}

// What Timesheets.tsx expects — matches the local TS interface
export interface MappedTimesheet {
  id:              string
  employeeId:      string
  employeeName:    string
  weekStarting:    string          // YYYY-MM-DD
  status:          'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'PROCESSED'
  submittedAt:     string | null   // not in DB schema — defaulted null
  approvedById:    string | null
  approvedAt:      string | null
  rejectedReason?: string
  // Pre-calculated payroll totals (for team summary view and team list)
  hoursRegular:    number
  hoursOvertime:   number
  hoursTotal:      number
  grossPay:        number | null
  paygAmount:      number | null
  superAmount:     number | null
  // Entries: empty until entry-level API is wired — UI shows blank days for Neon timesheets
  entries:         never[]
}

// DB status → UI status (collapse EXPORTED into PROCESSED for UI compatibility)
function mapStatus(s: string | undefined): MappedTimesheet['status'] {
  if (s === 'EXPORTED') return 'PROCESSED'
  if (s === 'APPROVED') return 'APPROVED'
  if (s === 'SUBMITTED') return 'SUBMITTED'
  if (s === 'PROCESSED') return 'PROCESSED'
  return 'DRAFT'
}

// YYYY-MM-DD from an ISO date string (handles both "2026-07-21" and "2026-07-21T00:00:00.000Z")
function toDateString(iso: string | null | undefined): string {
  if (!iso) return new Date().toISOString().slice(0, 10)
  return iso.slice(0, 10)
}

function num(v: string | number | null | undefined): number {
  if (v == null) return 0
  return typeof v === 'number' ? v : parseFloat(v as string) || 0
}

function mapApiTimesheet(raw: ApiTimesheet): MappedTimesheet {
  const weekStart = raw.weekStart ?? raw.week_start
  const empName   = raw.employee?.name ?? ''

  return {
    id:           raw.id,
    employeeId:   raw.employeeId ?? raw.employee_id ?? '',
    employeeName: empName,
    weekStarting: toDateString(weekStart),
    status:       mapStatus(raw.status),
    submittedAt:  null,   // not stored in DB — will be derived from status change timestamp later
    approvedById: raw.approvedById ?? raw.approved_by_id ?? null,
    approvedAt:   raw.approvedAt   ?? raw.approved_at   ?? null,
    hoursRegular:  num(raw.hoursRegular  ?? raw.hours_regular),
    hoursOvertime: num(raw.hoursOvertime ?? raw.hours_overtime),
    hoursTotal:    num(raw.hoursTotal    ?? raw.hours_total),
    grossPay:      raw.grossPay    ?? raw.gross_pay    ? num(raw.grossPay ?? raw.gross_pay) : null,
    paygAmount:    raw.paygAmount  ?? raw.payg_amount  ? num(raw.paygAmount ?? raw.payg_amount) : null,
    superAmount:   raw.superAmount ?? raw.super_amount ? num(raw.superAmount ?? raw.super_amount) : null,
    entries: [],
  }
}

export async function fetchTimesheets(params?: { employeeId?: string; jobId?: string; status?: string }): Promise<MappedTimesheet[]> {
  const query = new URLSearchParams()
  if (params?.employeeId) query.set('employeeId', params.employeeId)
  if (params?.jobId)      query.set('jobId',      params.jobId)
  if (params?.status)     query.set('status',     params.status)
  const qs = query.toString() ? `?${query.toString()}` : ''

  const { data } = await api.get<{ data: ApiTimesheet[] } | ApiTimesheet[]>(`/api/timesheets${qs}`)
  const raw: ApiTimesheet[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiTimesheet)
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Fire-and-forget status update — no-op for mock IDs (ts_1 etc.)
export async function updateTimesheetStatus(
  id: string,
  action: 'submit' | 'approve' | 'reject',
  opts?: { approvedById?: string; pmNotes?: string },
): Promise<void> {
  if (!UUID_RE.test(id)) return   // mock timesheet — skip
  try {
    if (action === 'approve') {
      await api.patch(`/api/timesheets/${id}/approve`, {
        approvedById: opts?.approvedById ?? 'system',
        pmNotes:      opts?.pmNotes ?? null,
      })
    } else {
      const status = action === 'submit' ? 'SUBMITTED' : 'REJECTED'
      await api.put(`/api/timesheets/${id}`, {
        status,
        pmNotes: opts?.pmNotes ?? null,
      })
    }
  } catch (err) {
    console.warn(`updateTimesheetStatus(${action}) failed for ${id}:`, err)
  }
}
